import capitalize from 'lodash/capitalize';
import { ReactNode } from 'react';

import { Box, Flex, Text, VStack, useColorModeValue } from '@vimeo/bokeh';
import {
  ChevronDownSmall,
  CircleExclamationFilled,
  EyeShut,
  LinkSecurityLock,
  LockPassword,
} from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { type PaywallTrackingProps } from '@vimeo/picox';
import {
  type SubscriptionPlans,
  useGetSubscriptionPlansData,
} from '@vimeo/subscription-api-plans-data-hook';

import { OtherPlansButton, PaywallCTAsWrapper, PurchaseButton } from '../components';

type LockedPrivacyOption = 'unlisted' | 'password' | 'hide_from_vimeo';

interface PrivacyLimitPaywallProps {
  isUserEligibleForFreeTrial?: boolean;
  tier?: Partial<SubscriptionPlans['tier']>;
  privacyOption?: LockedPrivacyOption;
  paywallTracking: PaywallTrackingProps;
}

export function PrivacyLimitPaywall({
  tier = 'standard',
  privacyOption = 'unlisted',
  paywallTracking,
  isUserEligibleForFreeTrial = false,
}: PrivacyLimitPaywallProps) {
  const plans = useGetSubscriptionPlansData([tier], undefined, false);
  const planData = plans?.find((plan) => plan.tier === tier) ?? plans?.[0];
  const hasTrialPath = Boolean(planData?.metadata?.interactions?.purchase?.uri?.freeTrial);
  const isTrial = hasTrialPath && isUserEligibleForFreeTrial;
  const resolvedPlanName = planData?.name ?? (tier ? capitalize(`${tier}`) : '');
  const privacyImageUrl = useColorModeValue(
    'https://i.vimeocdn.com/custom_asset/967fe8edf9f28375f1d3988ed28585d9?region=us',
    'https://i.vimeocdn.com/custom_asset/fd161e8907c7bd204bcdd8ca6379312a?region=us',
  );
  const privacyOptions = [
    {
      key: 'unlisted' as const,
      icon: <LinkSecurityLock />,
      title: translate('Unlisted'),
      description: translate('Only people with the link can view'),
    },
    {
      key: 'password' as const,
      icon: <LockPassword />,
      title: translate('Password'),
      description: translate('Only people with the password can view'),
    },
    {
      key: 'hide_from_vimeo' as const,
      icon: <EyeShut />,
      title: translate('Hide from Vimeo'),
      description: translate('Private on your account, but embeddable anywhere'),
    },
  ];

  const lockedOption =
    privacyOptions.find((option) => option.key === privacyOption) ?? privacyOptions[0];
  const backgroundOptions = privacyOptions.filter((option) => option.key !== lockedOption.key);
  const displayedOptions = [...backgroundOptions, lockedOption];

  return (
    <Flex
      direction="column"
      alignItems="center"
      width="100%"
      height={{ base: '100%', md: 'auto' }}
      maxHeight={{ base: 'none', md: 'calc(100vh - 120px)' }}
      position="relative"
    >
      <Flex
        direction="column"
        alignItems="center"
        width="100%"
        maxWidth="400px"
        maxHeight="100%"
        minHeight="0"
        gap="16px"
        flex="1 1 auto"
        pt={{ base: '24px', md: '0' }}
        pb="24px"
        mx={{ base: 'auto', md: '32px' }}
        overflowY="auto"
      >
        <Box
          width="192px"
          height="140px"
          minHeight="93px"
          flex="1 1 140px"
          backgroundImage={`url(${privacyImageUrl})`}
          backgroundSize="contain"
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          marginY="8px"
        />

        <Flex direction="column" alignItems="center" gap="16px" width="100%" paddingX="16px">
          <Text variant={{ base: 'heading-xl', md: 'heading-2xl' }} textAlign="center">
            {translate({
              singular: '{FEATURE_NAME} is not available',
              replacements: { FEATURE_NAME: lockedOption.title },
            })}
          </Text>
          <Text variant="heading-sm" color="text-primary" textAlign="center" paddingX="16px">
            {translate({
              singular:
                'This privacy feature isn’t included in your current plan. Upgrade to {PLAN_NAME} to use {FEATURE_NAME}.',
              replacements: { PLAN_NAME: resolvedPlanName, FEATURE_NAME: lockedOption.title },
            })}
          </Text>
        </Flex>

        <VStack width="100%" alignItems="stretch" position="relative" pt="24px" px="24px" gap="0">
          {displayedOptions.map((option, index) => (
            <PrivacyOptionRow
              key={option.key}
              icon={option.icon}
              title={option.title}
              description={option.description}
              contentOpacity={index === 0 ? 0.2 : index === 1 ? 0.5 : undefined}
              rowOffset={index === 0 ? '0px' : index === 1 ? '16px' : '32px'}
            />
          ))}
          <CircleExclamationFilled
            color="upsell-primary"
            width="40px"
            height="40px"
            transform="rotate(15deg)"
            position="absolute"
            right="9px"
            top="-9px"
          />
        </VStack>
      </Flex>

      <PaywallCTAsWrapper>
        <PurchaseButton
          subscriptionTier={tier}
          isTrial={isTrial}
          planName={resolvedPlanName}
          paywallTracking={paywallTracking}
        />
        <OtherPlansButton paywallTracking={paywallTracking} />
      </PaywallCTAsWrapper>
    </Flex>
  );
}

function PrivacyOptionRow({
  icon,
  title,
  description,
  contentOpacity,
  rowOffset = '0px',
}: {
  icon: ReactNode;
  title: string;
  description: string;
  contentOpacity?: number;
  rowOffset?: string;
}) {
  return (
    <Flex
      border="1px solid"
      borderColor="input-stroke"
      borderRadius="12px"
      px="16px"
      py="8px"
      alignItems="center"
      gap="8px"
      ml={rowOffset}
      width="calc(100% - 32px)"
      bg="surface"
      mb="-16px"
      zIndex="1"
    >
      <Box opacity={contentOpacity}>{icon}</Box>
      <Flex direction="column" alignItems="start" flex="1" minWidth="0" opacity={contentOpacity}>
        <Text variant="heading-xs" color="text-primary" width="100%">
          {title}
        </Text>
        <Text variant="body-md" color="text-secondary" width="100%">
          {description}
        </Text>
      </Flex>
      <Box opacity={contentOpacity}>
        <ChevronDownSmall />
      </Box>
    </Flex>
  );
}
